// Region-segregated access model (frontend copy).
// Keep in sync with server/lib/regions.js — CRA cannot import from outside /src,
// so the constants are intentionally duplicated. They are tiny and rarely change.

// A region maps to the set of ISO-3166 alpha-2 country codes it covers.
export const REGION_COUNTRIES: Record<string, string[]> = {
  germany: ['DE'],
  iberia: ['ES'], // Spain only (Portugal intentionally excluded)
  'italy-france': ['IT', 'FR'],
  switzerland: ['CH'],
}

// Okta group name -> region key. admin-* and user-* are identical in capability;
// the access scope is purely the region.
export const GROUP_REGION: Record<string, string> = {
  'admin-barrys-germany': 'germany',
  'user-barrys-germany': 'germany',
  'admin-barrys-iberia': 'iberia',
  'user-barrys-iberia': 'iberia',
  'admin-barrys-italy-france': 'italy-france',
  'user-barrys-italy-france': 'italy-france',
  'admin-barrys-switzerland': 'switzerland',
  'user-barrys-switzerland': 'switzerland',
}

/** Region keys a set of Okta groups grants access to. */
export function regionsForGroups(groups: string[] = []): string[] {
  const regions = new Set<string>()
  for (const g of groups) {
    const region = GROUP_REGION[g]
    if (region) regions.add(region)
  }
  return Array.from(regions)
}

/** Union of country codes (upper-case) a set of Okta groups grants access to. */
export function allowedCountries(groups: string[] = []): Set<string> {
  const countries = new Set<string>()
  for (const region of regionsForGroups(groups)) {
    for (const c of REGION_COUNTRIES[region] || []) countries.add(c)
  }
  return countries
}

/** Normalize a country value (code or name) to an ISO alpha-2 upper-case code. */
export function normalizeCountry(value?: string | null): string | null {
  if (!value) return null
  const v = String(value).trim()
  if (!v) return null
  if (v.length === 2) return v.toUpperCase()
  const byName: Record<string, string> = {
    germany: 'DE', deutschland: 'DE',
    spain: 'ES', españa: 'ES', espana: 'ES',
    italy: 'IT', italia: 'IT',
    france: 'FR',
    switzerland: 'CH', schweiz: 'CH', suisse: 'CH', svizzera: 'CH',
    portugal: 'PT',
    'united kingdom': 'GB', uk: 'GB',
    'united states': 'US', usa: 'US',
  }
  return byName[v.toLowerCase()] || v.toUpperCase()
}

/**
 * A customer is in scope if their home-studio country OR home-address country
 * is within the caller's allowed country set (OR semantics, default-deny).
 */
export function customerInScope(
  allowed: Set<string>,
  customer: { studioCountry?: string | null; addressCountry?: string | null }
): boolean {
  if (allowed.size === 0) return false
  const studio = normalizeCountry(customer.studioCountry)
  const address = normalizeCountry(customer.addressCountry)
  return (!!studio && allowed.has(studio)) || (!!address && allowed.has(address))
}
